import { useEffect, useState } from "react";
import { BellRing, Clock, CheckCircle2, Ban, Loader2, X, RotateCw, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Trader-facing reminders view.
 *
 * Reminders can be created via the chat ("remind me tomorrow at 8am to ...") and
 * are also delivered via WhatsApp + email. This page surfaces them all — so a
 * user who has no phone (email-only sign-in) still sees what's coming.
 */
export default function Reminders() {
  const [data, setData] = useState({ reminders: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}&limit=200` : "?limit=200";
      const r = await api.get(`/reminders${q}`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const patch = async (id, changes) => {
    try {
      await api.patch(`/reminders/${id}`, changes);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reminder?")) return;
    try {
      await api.delete(`/reminders/${id}`);
      toast.success("Reminder deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const CHIPS = [
    { k: "pending", l: "Pending", n: data.counts.pending || 0, tone: "bg-amber-100 text-amber-800 border-amber-200" },
    { k: "sent", l: "Sent", n: data.counts.sent || 0, tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    { k: "done", l: "Done", n: data.counts.done || 0, tone: "bg-indigo-100 text-indigo-800 border-indigo-200" },
    { k: "cancelled", l: "Cancelled", n: data.counts.cancelled || 0, tone: "bg-gray-100 text-gray-700 border-gray-200" },
  ];

  return (
    <div className="px-4 py-5 space-y-4 pb-8" data-testid="reminders-page">
      <div>
        <div className="text-xs text-kudi-ink/55 uppercase tracking-wider font-semibold">Reminders</div>
        <h1 className="font-heading text-2xl font-bold mt-1 flex items-center gap-2">
          <BellRing className="w-5 h-5 text-amber-500" /> Your reminders
        </h1>
        <p className="text-sm text-kudi-ink/60 mt-1">Delivered via WhatsApp and email — and always visible here.</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {CHIPS.map((c) => (
          <button key={c.k} onClick={() => setFilter(c.k)}
            className={`text-xs px-3 py-1.5 rounded-full border font-medium shrink-0 transition ${filter === c.k ? c.tone : "bg-white border-border text-kudi-ink/60"}`}
            data-testid={`filter-${c.k}`}
          >
            {c.l} <span className="opacity-70 ml-1">{c.n}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-kudi-ink/40 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : data.reminders.length === 0 ? (
        <div className="bg-white border border-border rounded-2xl p-8 text-center" data-testid="reminders-empty">
          <BellRing className="w-10 h-10 mx-auto text-kudi-ink/25 mb-3" />
          <div className="font-heading font-semibold">No {filter} reminders</div>
          <p className="text-sm text-kudi-ink/60 mt-1 max-w-xs mx-auto">
            Ask Kobolog in chat — say <span className="font-mono bg-kudi-cream px-1.5 py-0.5 rounded">remind me tomorrow at 8am to call Musa</span>.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="reminders-list">
          {data.reminders.map((r) => <ReminderRow key={r.id} r={r} onDone={() => patch(r.id, { status: "done" })} onCancel={() => patch(r.id, { status: "cancelled" })} onReopen={() => patch(r.id, { due_at: r.due_at })} onDelete={() => remove(r.id)} />)}
        </ul>
      )}
    </div>
  );
}

function ReminderRow({ r, onDone, onCancel, onReopen, onDelete }) {
  const due = r.due_at ? new Date(r.due_at) : null;
  const overdue = r.status === "pending" && due && due < new Date();
  const isClosed = r.status === "done" || r.status === "cancelled";
  const statusStyle = {
    pending: { bg: "bg-amber-50 border-amber-200", chip: "bg-amber-500 text-white", icon: Clock },
    sent: { bg: "bg-emerald-50 border-emerald-200", chip: "bg-emerald-500 text-white", icon: CheckCircle2 },
    done: { bg: "bg-indigo-50 border-indigo-200", chip: "bg-indigo-500 text-white", icon: CheckCircle2 },
    cancelled: { bg: "bg-gray-50 border-gray-200", chip: "bg-gray-400 text-white", icon: Ban },
    error: { bg: "bg-rose-50 border-rose-200", chip: "bg-rose-500 text-white", icon: AlertCircle },
  }[r.status] || { bg: "bg-white border-border", chip: "bg-gray-400 text-white", icon: Clock };
  const S = statusStyle.icon;

  return (
    <li className={`rounded-2xl border p-4 ${overdue ? "bg-rose-50/60 border-rose-200" : statusStyle.bg}`} data-testid={`reminder-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg ${statusStyle.chip} flex items-center justify-center shrink-0`}>
          <S className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${isClosed ? "text-kudi-ink/50 line-through" : ""}`}>{r.task}</div>
          <div className="text-[11px] text-kudi-ink/55 mt-1 flex items-center gap-2 flex-wrap">
            {due && (
              <span className={overdue ? "text-rose-600 font-semibold" : ""}>
                {due.toLocaleString("en-NG", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                {overdue && " · OVERDUE"}
              </span>
            )}
            {r.delivered_via && <span className="capitalize text-emerald-700">✓ sent via {r.delivered_via}</span>}
            {r.delivery_attempts > 0 && r.status === "pending" && (
              <span className="text-amber-700">· {r.delivery_attempts} delivery attempt{r.delivery_attempts > 1 ? "s" : ""}</span>
            )}
          </div>
          {r.last_error && r.status === "pending" && (
            <div className="text-[11px] text-rose-600 mt-1">
              Couldn&apos;t deliver: {r.last_error.length > 80 ? r.last_error.slice(0, 80) + "…" : r.last_error}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex gap-2 flex-wrap">
        {!isClosed && (
          <>
            <button onClick={onDone} className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-indigo-700 inline-flex items-center gap-1" data-testid={`done-${r.id}`}>
              <CheckCircle2 className="w-3 h-3" /> Done
            </button>
            <button onClick={onCancel} className="text-xs bg-white border border-border text-kudi-ink/70 px-3 py-1.5 rounded-lg font-semibold hover:border-rose-300 hover:text-rose-600 inline-flex items-center gap-1" data-testid={`cancel-${r.id}`}>
              <Ban className="w-3 h-3" /> Cancel
            </button>
          </>
        )}
        {isClosed && (
          <button onClick={onReopen} className="text-xs bg-white border border-border text-kudi-ink/70 px-3 py-1.5 rounded-lg font-semibold hover:border-amber-300 hover:text-amber-700 inline-flex items-center gap-1" data-testid={`reopen-${r.id}`}>
            <RotateCw className="w-3 h-3" /> Reopen
          </button>
        )}
        <button onClick={onDelete} className="text-xs text-kudi-ink/40 px-2 py-1.5 hover:text-rose-600 inline-flex items-center gap-1 ml-auto" data-testid={`delete-${r.id}`}>
          <X className="w-3 h-3" /> Delete
        </button>
      </div>
    </li>
  );
}
